/**
 * @todo
 * - Consider using data-attribute (style + dynamic container)
 *   instead of {@link COUNT_MODAL} and {@link COUNT_TOTAL}.
 *   This could allow optional custom target selector.
 * - Timeout-Styles (const CSS_NEGLECT = "popup-neglect";)
 * - Optional custom target selector for {@link show}
 * - Evaluate enter/leave animations.
 *
 * @module
 */
import { EVENT_CLICK, addClass, animate, append, clearTimeout, createElement, createPromise, document, noop, off, removeClass, removeNode, setText, setTimeout, window, } from "@acng/frontend-bounty";
import { onClickElement } from "@acng/frontend-bounty/dom/event.js";
import { closest, isAncestorOf } from "@acng/frontend-bounty/dom/query.js";
/**
 * @param {"error" | "warn" | "info"} severity
 * @param {string} message
 * @returns {Promise<Element>}
 */
let noteElementLoader = async (severity, message) => {
    const element = createElement("p");
    addClass(element, "note", severity);
    setText(element, message);
    return element;
};
/**
 * @param {typeof noteElementLoader} loader
 * @returns {void}
 */
export const setNoteElementLoader = (loader) => {
    noteElementLoader = loader;
};
/**
 * @param {Element} element
 * @returns {Promise<void>}
 */
const onLeaveDefault = (element) => animate(element, [{ opacity: "1" }, { opacity: "0" }], { duration: 250 }).finished.then(noop, noop);
/**
 * @param {Element} element
 * @returns {Promise<void>}
 */
const onEnterDefault = (element) => animate(element, [{ opacity: "0" }, { opacity: "1" }], { duration: 250 }).finished.then(noop, noop);
const CONTAINER = Symbol();
const PARENT_NODE = Symbol();
const COUNT_MODAL = Symbol();
const COUNT_TOTAL = Symbol();
const SEVERITY_INFO = "info";
const SEVERITY_WARN = "warn";
const SEVERITY_ERROR = "error";
const CSS_MODAL = "popup-modal";
const CSS_EMPTY = "popup-empty";
/**
 * @type {WeakMap<Element, (value: unknown) => Promise<void>>}
 */
const openedElements = new WeakMap();
export class PopupController {
    /**
     * @param {Element | null} container
     */
    constructor(container) {
        this[CONTAINER] = container;
        this[PARENT_NODE] = container ?? document.createDocumentFragment();
        this[COUNT_TOTAL] = 0;
        this[COUNT_MODAL] = 0;
    }
    /**
     * @param {Element} element
     * @param {(element: Element) => Promise<void>} [onEnter]
     * @param {(element: Element) => Promise<void>} [onLeave]
     * @returns {Promise<unknown>}
     */
    show(element, onEnter, onLeave) {
        return show(this, element, false, onEnter, onLeave);
    }
    /**
     * @param {Element} element
     * @param {(element: Element) => Promise<void>} [onEnter]
     * @param {(element: Element) => Promise<void>} [onLeave]
     * @returns {Promise<unknown>}
     */
    showModal(element, onEnter, onLeave) {
        return show(this, element, true, onEnter, onLeave);
    }
    /**
     * @param {string} text
     */
    async [SEVERITY_INFO](text) {
        return show(this, await noteElementLoader(SEVERITY_INFO, text));
    }
    /**
     * @param {string} text
     */
    async [SEVERITY_WARN](text) {
        return show(this, await noteElementLoader(SEVERITY_WARN, text));
    }
    /**
     * @param {string} text
     */
    async [SEVERITY_ERROR](text) {
        return show(this, await noteElementLoader(SEVERITY_ERROR, text));
    }
    /**
     * @param {Element} element
     * @returns {boolean}
     */
    isOpen(element) {
        return openedElements.has(element);
    }
    /**
     * @param {Element} element
     * @param {unknown} [value]
     * @returns {Promise<void>}
     */
    async close(element, value) {
        return await openedElements.get(element)?.(value);
    }
}
/**
 * @param {PopupController} popup
 * @param {Element | null} container
 * @returns {void}
 */
export const setContainer = (popup, container) => {
    if (container) {
        append(container, ...popup[PARENT_NODE].childNodes);
        popup[PARENT_NODE] = container;
        if (popup[COUNT_MODAL]) {
            addClass(container, CSS_MODAL);
        }
    }
    else {
        popup[PARENT_NODE] = document.createDocumentFragment();
        if (popup[CONTAINER]) {
            append(popup[PARENT_NODE], ...popup[CONTAINER].children);
        }
    }
    popup[CONTAINER] = container;
};
/**
 * Closure style since performance is not much of importance.
 *
 * @param {PopupController} popup
 * @param {Element} element
 * @param {boolean} [modal]
 * @param {(child: Element) => Promise<void>} [onEnter]
 * @param {(child: Element) => Promise<void>} [onLeave]
 */
const show = (popup, element, modal, onEnter, onLeave) => {
    return createPromise((resolve) => {
        let timeout = 0;
        /**
         * @type {?(ev: Event) => void}
         */
        let globalListener = null;
        const localListener = onClickElement(element, (target) => closest(element, target, "[data-popup-close]", closeFn));
        /**
         * @param {unknown} [value]
         */
        const closeFn = async (value) => {
            if (timeout) {
                clearTimeout(timeout);
            }
            else if (globalListener) {
                off(window, EVENT_CLICK, globalListener);
            }
            off(element, EVENT_CLICK, localListener);
            await (onLeave ?? onLeaveDefault)(element);
            removeNode(element);
            openedElements.delete(element);
            if (!--popup[COUNT_TOTAL] && popup[CONTAINER]) {
                addClass(popup[CONTAINER], CSS_EMPTY);
            }
            if (modal && !--popup[COUNT_MODAL] && popup[CONTAINER]) {
                removeClass(popup[CONTAINER], CSS_MODAL);
            }
            resolve(value);
        };
        append(popup[PARENT_NODE], element);
        openedElements.set(element, closeFn);
        popup[COUNT_TOTAL]++;
        if (popup[CONTAINER]) {
            removeClass(popup[CONTAINER], CSS_EMPTY);
        }
        if (modal) {
            popup[COUNT_MODAL]++;
            if (popup[CONTAINER]) {
                addClass(popup[CONTAINER], CSS_MODAL);
            }
        }
        else {
            timeout = setTimeout(() => {
                timeout = 0;
                globalListener = onClickElement(window, (target) => {
                    if (target !== element && !isAncestorOf(element, target)) {
                        closeFn();
                    }
                });
            }, 5000);
        }
        (onEnter ?? onEnterDefault)(element);
    });
};
